import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';

/** Points a single control at its error text and marks it invalid, so the
 *  message is announced with the field instead of floating beside it. A child
 *  that already names its own description is left alone. */
export function linkControlToError(
  children: ReactNode,
  errorId: string,
  error: string | undefined,
): ReactNode {
  if (!error || !isValidElement(children)) return children;
  const control = children as ReactElement<Record<string, unknown>>;
  if (control.props['aria-describedby']) return children;
  return cloneElement(control, { 'aria-describedby': errorId, 'aria-invalid': true });
}

/** Names a single control by its visible label and marks it required, for
 *  controls (a custom select, a group) that no `<label>` wraps. A child that
 *  already has its own name is left alone. */
export function labelControl(children: ReactNode, labelId: string, required: boolean): ReactNode {
  if (!isValidElement(children)) return children;
  const control = children as ReactElement<Record<string, unknown>>;
  if (control.props['aria-label'] || control.props['aria-labelledby']) return children;
  return cloneElement(control, {
    'aria-labelledby': labelId,
    ...(required ? { 'aria-required': true } : {}),
  });
}
